"use client"

import { useState } from "react"
import {
  Search,
  TrendingUp,
  Target,
  Clock,
  Users,
  Award,
  BookOpen,
  ChevronRight,
  ArrowRight,
  CheckCircle,
  Briefcase,
  Building,
} from "lucide-react"
import { cn } from "@/utils/common"
import { Badge } from "@/components/ui/common/Badge"
import { Input } from "@/components/forms/inputs/text/Input"
import { Button } from "@/components/ui/common/Button"

interface CareerPath {
  id: number
  title: string
  category: string
  description: string
  currentLevel: "Junior" | "Mid" | "Senior" | "Expert"
  targetLevel: "Mid" | "Senior" | "Expert" | "Lead"
  timeToAchieve: string
  difficulty: "Łatwy" | "Średni" | "Trudny"
  steps: {
    title: string
    description: string
    duration: string
    completed: boolean
  }[]
  requiredSkills: string[]
  salaryRange: string
  demandLevel: "Wysoki" | "Średni" | "Niski"
  companies: string[]
  icon: any
  color: string
  matchPercentage: number
}

export default function CareerPaths() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("Wszystkie")
  const [selectedPath, setSelectedPath] = useState<CareerPath | null>(null)

  const careerPaths: CareerPath[] = [
    {
      id: 1,
      title: "Frontend Developer → Tech Lead",
      category: "IT",
      description:
        "Ścieżka rozwoju od programisty frontend do lidera technicznego zespołu, obejmująca zarówno umiejętności techniczne jak i zarządcze.",
      currentLevel: "Mid",
      targetLevel: "Lead",
      timeToAchieve: "2-3 lata",
      difficulty: "Średni",
      steps: [
        {
          title: "Pogłębienie znajomości React/Vue",
          description: "Opanowanie zaawansowanych wzorców i optymalizacji",
          duration: "3 miesiące",
          completed: true,
        },
        {
          title: "Nauka architektury aplikacji",
          description: "Zrozumienie wzorców projektowych i architektury frontend",
          duration: "4 miesiące",
          completed: true,
        },
        {
          title: "Rozwój umiejętności mentorskich",
          description: "Mentoring junior developerów i code review",
          duration: "6 miesięcy",
          completed: false,
        },
        {
          title: "Zarządzanie projektami",
          description: "Prowadzenie projektów i koordynacja zespołu",
          duration: "8 miesięcy",
          completed: false,
        },
        {
          title: "Strategia techniczna",
          description: "Planowanie długoterminowej strategii technologicznej",
          duration: "6 miesięcy",
          completed: false,
        },
      ],
      requiredSkills: ["React/Vue", "TypeScript", "Architektura", "Leadership", "Komunikacja"],
      salaryRange: "15 000 - 25 000 zł",
      demandLevel: "Wysoki",
      companies: ["Google", "Microsoft", "Allegro", "Asseco"],
      icon: TrendingUp,
      color: "blue",
      matchPercentage: 92,
    },
    {
      id: 2,
      title: "Marketing Specialist → Marketing Manager",
      category: "Marketing",
      description:
        "Rozwój od specjalisty marketingu do managera, z fokusem na marketing cyfrowy i zarządzanie kampaniami.",
      currentLevel: "Junior",
      targetLevel: "Senior",
      timeToAchieve: "3-4 lata",
      difficulty: "Średni",
      steps: [
        {
          title: "Certyfikacja Google Ads",
          description: "Uzyskanie certyfikatów Google Ads i Analytics",
          duration: "2 miesiące",
          completed: true,
        },
        {
          title: "Zarządzanie budżetem kampanii",
          description: "Samodzielne prowadzenie kampanii reklamowych",
          duration: "6 miesięcy",
          completed: false,
        },
        {
          title: "Analiza danych i ROI",
          description: "Zaawansowana analiza skuteczności kampanii",
          duration: "4 miesiące",
          completed: false,
        },
        {
          title: "Zarządzanie zespołem",
          description: "Prowadzenie zespołu marketingowego",
          duration: "8 miesięcy",
          completed: false,
        },
        {
          title: "Strategia marketingowa",
          description: "Tworzenie długoterminowych strategii marketingowych",
          duration: "6 miesięcy",
          completed: false,
        },
      ],
      requiredSkills: ["Google Ads", "Analytics", "SEO", "Content Marketing", "Zarządzanie"],
      salaryRange: "8 000 - 15 000 zł",
      demandLevel: "Wysoki",
      companies: ["Ogilvy", "Publicis", "Wirtualna Polska", "Allegro"],
      icon: Target,
      color: "green",
      matchPercentage: 85,
    },
    {
      id: 3,
      title: "Data Analyst → Data Scientist",
      category: "Data Science",
      description: "Przejście od analizy danych do zaawansowanego modelowania predykcyjnego i machine learning.",
      currentLevel: "Mid",
      targetLevel: "Senior",
      timeToAchieve: "2-3 lata",
      difficulty: "Trudny",
      steps: [
        {
          title: "Python i biblioteki ML",
          description: "Opanowanie pandas, scikit-learn, TensorFlow",
          duration: "4 miesiące",
          completed: true,
        },
        {
          title: "Statystyka i matematyka",
          description: "Pogłębienie wiedzy statystycznej i matematycznej",
          duration: "6 miesięcy",
          completed: false,
        },
        {
          title: "Projekty ML end-to-end",
          description: "Realizacja kompletnych projektów machine learning",
          duration: "8 miesięcy",
          completed: false,
        },
        {
          title: "Big Data i chmura",
          description: "Praca z dużymi zbiorami danych w chmurze",
          duration: "4 miesiące",
          completed: false,
        },
        {
          title: "Wdrażanie modeli",
          description: "MLOps i wdrażanie modeli do produkcji",
          duration: "6 miesięcy",
          completed: false,
        },
      ],
      requiredSkills: ["Python", "Machine Learning", "Statystyka", "SQL", "Cloud"],
      salaryRange: "12 000 - 22 000 zł",
      demandLevel: "Wysoki",
      companies: ["Netflix", "Amazon", "PKO BP", "mBank"],
      icon: Award,
      color: "purple",
      matchPercentage: 78,
    },
    {
      id: 4,
      title: "UX Designer → Head of Design",
      category: "Design",
      description:
        "Rozwój od projektanta UX do kierownika działu designu, łącząc umiejętności projektowe z zarządczymi.",
      currentLevel: "Senior",
      targetLevel: "Lead",
      timeToAchieve: "2-3 lata",
      difficulty: "Średni",
      steps: [
        {
          title: "Design System",
          description: "Tworzenie i zarządzanie systemami projektowymi",
          duration: "3 miesiące",
          completed: true,
        },
        {
          title: "Research i testowanie",
          description: "Zaawansowane metody badań użytkowników",
          duration: "4 miesiące",
          completed: true,
        },
        {
          title: "Mentoring designerów",
          description: "Rozwój umiejętności mentorskich i feedbacku",
          duration: "6 miesięcy",
          completed: false,
        },
        {
          title: "Strategia produktowa",
          description: "Wpływ na strategię produktu i biznesu",
          duration: "8 miesięcy",
          completed: false,
        },
        {
          title: "Zarządzanie działem",
          description: "Prowadzenie zespołu designerów",
          duration: "6 miesięcy",
          completed: false,
        },
      ],
      requiredSkills: ["Figma", "User Research", "Design Systems", "Leadership", "Strategy"],
      salaryRange: "12 000 - 20 000 zł",
      demandLevel: "Średni",
      companies: ["Spotify", "Airbnb", "Allegro", "Asseco"],
      icon: Briefcase,
      color: "pink",
      matchPercentage: 81,
    },
    {
      id: 5,
      title: "HR Specialist → HR Business Partner",
      category: "HR",
      description: "Rozwój od specjalisty HR do strategicznego partnera biznesowego, wspierającego cele organizacji.",
      currentLevel: "Mid",
      targetLevel: "Senior",
      timeToAchieve: "3-4 lata",
      difficulty: "Średni",
      steps: [
        {
          title: "Analiza danych HR",
          description: "Wykorzystanie analytics w procesach HR",
          duration: "3 miesiące",
          completed: false,
        },
        {
          title: "Zarządzanie talentami",
          description: "Strategie pozyskiwania i rozwoju talentów",
          duration: "6 miesięcy",
          completed: false,
        },
        {
          title: "Business partnering",
          description: "Współpraca z działami biznesowymi",
          duration: "8 miesięcy",
          completed: false,
        },
        {
          title: "Change management",
          description: "Zarządzanie zmianą organizacyjną",
          duration: "6 miesięcy",
          completed: false,
        },
        {
          title: "Strategia HR",
          description: "Tworzenie strategii HR zgodnej z celami biznesowymi",
          duration: "8 miesięcy",
          completed: false,
        },
      ],
      requiredSkills: ["HR Analytics", "Talent Management", "Business Acumen", "Change Management"],
      salaryRange: "10 000 - 18 000 zł",
      demandLevel: "Średni",
      companies: ["Deloitte", "PwC", "Microsoft", "Samsung"],
      icon: Users,
      color: "orange",
      matchPercentage: 73,
    },
  ]

  const categories = ["Wszystkie", "IT", "Marketing", "Data Science", "Design", "HR"]

  const filteredPaths = careerPaths.filter((path) => {
    const matchesSearch =
      path.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      path.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === "Wszystkie" || path.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case "Łatwy":
        return "text-green-600 bg-green-50 border-green-200"
      case "Średni":
        return "text-yellow-600 bg-yellow-50 border-yellow-200"
      case "Trudny":
        return "text-red-600 bg-red-50 border-red-200"
      default:
        return "text-gray-600 bg-gray-50 border-gray-200"
    }
  }

  const getDemandColor = (level: string) => {
    switch (level) {
      case "Wysoki":
        return "text-green-600 bg-green-50 border-green-200"
      case "Średni":
        return "text-yellow-600 bg-yellow-50 border-yellow-200"
      case "Niski":
        return "text-red-600 bg-red-50 border-red-200"
      default:
        return "text-gray-600 bg-gray-50 border-gray-200"
    }
  }

  if (selectedPath) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <button
              onClick={() => setSelectedPath(null)}
              className="flex items-center text-[#2bcaca] hover:text-[#229999] mb-4"
            >
              <ChevronRight className="h-4 w-4 mr-1 transform rotate-180" />
              Powrót do ścieżek kariery
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <selectedPath.icon className="h-8 w-8 text-[#2bcaca] mr-3" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{selectedPath.title}</h1>
                  <p className="text-gray-600 mt-1">{selectedPath.category}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#2bcaca]">{selectedPath.matchPercentage}%</div>
                <p className="text-sm text-gray-500">dopasowanie</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <div className="bg-white p-6  shadow-sm border border-gray-200">
                <h2 className="text-xl font-semibold mb-4">Opis ścieżki</h2>
                <p className="text-gray-700 leading-relaxed">{selectedPath.description}</p>
              </div>

              {/* Steps */}
              <div className="bg-white p-6 shadow-sm border border-gray-200">
                <h2 className="text-xl font-semibold mb-4">Plan rozwoju</h2>
                <div className="space-y-4">
                  {selectedPath.steps.map((step, index) => (
                    <div key={index} className="flex items-start p-4 border border-gray-200 rounded-lg">
                      <div className="flex-shrink-0 mr-4">
                        {step.completed ? (
                          <CheckCircle className="h-6 w-6 text-[#2bcaca]" />
                        ) : (
                          <div className="h-6 w-6 border-2 border-[#2bcaca] rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-[#2bcaca]">{index + 1}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">{step.title}</h3>
                        <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-[#2bcaca] mr-1" />
                          <span className="text-xs text-gray-500">{step.duration}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Required Skills */}
              <div className="bg-white p-6 shadow-sm border border-gray-200">
                <h2 className="text-xl font-semibold mb-4">Wymagane umiejętności</h2>
                <div className="flex flex-wrap gap-2">
                  {selectedPath.requiredSkills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="bg-[#2bcaca]/10 text-[#2bcaca] border-[#2bcaca]/20">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Companies */}
              <div className="bg-white p-6 shadow-sm border border-gray-200">
                <h2 className="text-xl font-semibold mb-4">Przykładowe firmy</h2>
                <div className="grid grid-cols-2 gap-3">
                  {selectedPath.companies.map((company, index) => (
                    <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg">
                      <Building className="h-4 w-4 text-[#2bcaca] mr-2" />
                      <span className="text-sm text-gray-700">{company}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="bg-white p-6 shadow-sm border border-gray-200">
                <h2 className="text-xl font-semibold mb-4">Informacje</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Poziom obecny:</span>
                    <Badge variant="outline" className="bg-[#2bcaca]/10 text-[#2bcaca] border-[#2bcaca]/20">
                      {selectedPath.currentLevel}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Poziom docelowy:</span>
                    <Badge variant="outline" className="bg-[#2bcaca]/10 text-[#2bcaca] border-[#2bcaca]/20">
                      {selectedPath.targetLevel}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Czas osiągnięcia:</span>
                    <span className="font-medium">{selectedPath.timeToAchieve}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Trudność:</span>
                    <Badge variant="outline" className={getDifficultyColor(selectedPath.difficulty)}>
                      {selectedPath.difficulty}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Zapotrzebowanie:</span>
                    <Badge variant="outline" className={getDemandColor(selectedPath.demandLevel)}>
                      {selectedPath.demandLevel}
                    </Badge>
                  </div>

                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Zarobki docelowe:</span>
                      <span className="font-medium">{selectedPath.salaryRange}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress */}
              <div className="bg-white p-6 shadow-sm border border-gray-200">
                <h2 className="text-xl font-semibold mb-4">Postęp</h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Ukończone kroki:</span>
                    <span className="font-medium">
                      {selectedPath.steps.filter((step) => step.completed).length} / {selectedPath.steps.length}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-[#2bcaca] h-2 rounded-full"
                      style={{
                        width: `${(selectedPath.steps.filter((step) => step.completed).length / selectedPath.steps.length) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="bg-white p-6  border-gray-200 shadow-sm border">
                <h2 className="text-xl font-semibold mb-4">Działania</h2>
                <div className="space-y-3">
                  <Button className="w-full bg-[#2bcaca] hover:bg-[#229999] text-white">
                    <Target className="h-4 w-4 mr-2" />
                    Rozpocznij ścieżkę
                  </Button>
                  <Button variant="outline" className="w-full">
                    <BookOpen className="h-4 w-4 mr-2" />
                    Materiały do nauki
                  </Button>
                  <Button variant="outline" className="w-full">
                    <Users className="h-4 w-4 mr-2" />
                    Znajdź mentora
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Ścieżki kariery</h1>
          <p className="text-gray-600">Zaplanuj swój rozwój zawodowy i osiągnij cele kariery</p>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Szukaj ścieżki kariery..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-200"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={cn(
                    selectedCategory === category
                      ? "bg-[#2bcaca] hover:bg-[#229999] text-white"
                      : "text-gray-600 hover:text-gray-900",
                  )}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPaths.map((path) => (
            <div
              key={path.id}
              className="bg-white p-6  shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedPath(path)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start">
                  <path.icon className="h-6 w-6 text-[#2bcaca] mr-3 mt-1" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{path.title}</h3>
                    <p className="text-sm text-gray-500">{path.category}</p>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <div className="text-lg font-bold text-[#2bcaca]">{path.matchPercentage}%</div>
                  <p className="text-xs text-gray-500">dopasowanie</p>
                </div>
              </div>

              {/* Description */}
              <p className="text-gray-700 text-sm mb-4 line-clamp-3">{path.description}</p>

              {/* Progress */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Postęp:</span>
                  <span className="font-medium">
                    {path.steps.filter((step) => step.completed).length} / {path.steps.length} kroków
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#2bcaca] h-2 rounded-full"
                    style={{
                      width: `${(path.steps.filter((step) => step.completed).length / path.steps.length) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Czas osiągnięcia:</span>
                  <span className="font-medium">{path.timeToAchieve}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Trudność:</span>
                  <Badge variant="outline" className={getDifficultyColor(path.difficulty)} size="sm">
                    {path.difficulty}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Zarobki docelowe:</span>
                  <span className="font-medium text-sm">{path.salaryRange}</span>
                </div>
              </div>

              {/* Action */}
              <Button variant="outline" size="sm" className="w-full">
                <ArrowRight className="h-4 w-4 mr-2" />
                Zobacz szczegóły
              </Button>
            </div>
          ))}
        </div>

        {filteredPaths.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Nie znaleziono ścieżek kariery spełniających kryteria wyszukiwania.</p>
          </div>
        )}
      </div>
    </div>
  )
}
